/**
 * Accept-invite mutation hook — task 104d.2 D2.5.
 *
 * Calls `POST /api/v1/invites/accept` with the invite token. Fires in two
 * scenarios:
 *   1. Already-signed-in: user clicks "Continue as …" on
 *      `/invites/accept?token=…`.
 *   2. Post-sign-up: the Clerk `<SignUp>` widget finishes, `useUser()`
 *      reports `isSignedIn=true`, and the page fires this mutation.
 *
 * The call requires an authenticated Clerk session (the endpoint is in
 * `AUTHENTICATED_NO_ORG_PATHS` — no org required, but the Bearer token is
 * mandatory). Token comes from AppContext's `getToken`, populated by
 * `ClerkSync` in live mode.
 *
 * On success the caller should navigate to `/` (the authenticated root,
 * which redirects to `/dashboard`). We don't redirect here so the page
 * can render a success state / transition animation if it chooses.
 */

import { useMutation } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';

export interface AcceptInviteResult {
  membership: { id: string; orgId: string; role: 'owner' | 'admin' | 'member' };
  employee?: { id: string };
}

export function useAcceptInvite() {
  const { mockupMode } = useAppContext();
  const getToken = useToken();

  return useMutation({
    mutationFn: async (token: string) => {
      if (mockupMode) return { mockup: true as const };
      const sessionToken = await getToken();
      return apiFetch<AcceptInviteResult>(`/api/v1/invites/accept`, sessionToken, {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    },
  });
}
